import React, { useEffect, useImperativeHandle, useMemo, useRef, useState, useContext } from 'react';
import {
  campaignTypeGroupBy,
  OverrideContext
} from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/AdMetric/AdMetricBarChart';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useStacklineTheme } from '@stackline/ui';
import { useAppSelector, useGetRetailerUrl, useMetricFormatter } from 'src/utils/Hooks';
import ReactDOMServer from 'react-dom/server';
import { ChevronIconV2 } from 'src/components/SvgIcons';
import _get from 'lodash/get';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import _merge from 'lodash/merge';
import { generateSearchLink } from 'src/utils/searchLinks';
import { useRetailerId } from 'src/utils/Hooks/reduxSelectors';
import { Box, SxProps } from '@mui/material';
import { EntityCategoryDatum, XAxisCategory } from 'src/components/BeaconV2Components/TopEntityChartV2/types';
import _cloneDeep from 'lodash/cloneDeep';
import NoDataPlaceHolder from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import DefaultXAxisLabel from 'src/components/BeaconV2Components/TopEntityChartV2/DefaultXAxisLabel';
import { getAppName } from 'src/utils/app';
import { AppName } from 'sl-api-connector';

const DEFAULT_TOP_ENTITY_HEIGHT = 420;

export interface TopEntityChartApi {
  setRangeStart: (newRangeStart: number) => void;
}

export interface TopEntityChartRef {
  api: TopEntityChartApi;
}

export interface TopEntityV2ChartProps {
  /**
   * Topentity data to display
   */
  chartSeries: (Highcharts.SeriesOptionsType & { metricType?: string })[] & { categories?: { type?: string }[] }[];
  forecastedData?: Highcharts.SeriesOptionsType[];
  chartOptionsOverride?: Partial<Highcharts.Options> | ((defaultOptions: Highcharts.Options) => Highcharts.Options);
  redirectToSamePage?: boolean;
  /**
   * Specify the color for each series.
   * TODO we should remove this and use chartSeries to define the colors,
   * we still need to remove old Widget bar charts across beacon pro first
   * since some of them override with an incorrect color.
   */
  seriesColors?: string[];
  /**
   * When true, (default) the bars will be left aligned, when false, the bars will be centered
   */
  leftAlignBars?: boolean;
  containerSx?: SxProps;

  /**
   * Optional formatter for the xAxis labels. Components must use ReactDOMServer.renderToStaticMarkup to render the label as stringified HTML.
   */
  xAxisFormatter?: (value: EntityCategoryDatum, id?: string, tooltipId?: string) => string;
  /**
   * Optional additional CSS styles for the xAxis label text
   */
  xAxisLabelTextStyles?: Highcharts.CSSObject;
  /**
   * Optional function to get the categories for the xAxis. If not provided, the categories will be derived from the chartSeries data.
   */
  getCategories?: (series: Highcharts.SeriesOptionsType[]) => XAxisCategory[];

  /**
   * Optional flag to use a static category on the x-axis.
   * Used for keep the latest week on the right side of the chart even while scrolling horizontally.
   */
  useStaticCategory?: boolean;

  /**
   * Reverses the starting scroll position of the xAxis. Default is false.
   */
  reverseAxisStartPosition?: boolean;

  // determine how many datapoints will be available in a window without scrolling
  maxDataPointsToShow?: number;
}

/**
 * Example:
 * 'sponsoredProducts' => 'Sponsored Products'
 * 'sponsoredBrands' => 'Sponsored Brands'
 */
const formatCapitalizedString = (input: string) => {
  const withSpaces = input.replace(/([A-Z])/g, ' $1');
  const formattedWords = withSpaces ? withSpaces.charAt(0)?.toUpperCase() + withSpaces?.slice(1) : undefined;
  return formattedWords;
};

const seriesMarker = {
  enabled: false,
  symbol: 'circle',
  states: {
    hover: {
      radius: 4,
      lineWidth: 0
    }
  }
};

export const generateLink = (
  currentGroupBy: string | undefined,
  retailerId: string | undefined,
  displayName: string | undefined,
  linkId: string | undefined,
  searchParams: string,
  redirectToSamePage: boolean,
  additionalParams: string
): string | undefined => {
  let overrideGroupBy = currentGroupBy;
  if (currentGroupBy === 'searchterm') {
    return generateSearchLink(retailerId, displayName);
  }

  if (currentGroupBy === 'campaign') {
    overrideGroupBy = 'adCampaign';
  }

  if (currentGroupBy === 'entity') {
    overrideGroupBy = 'adEntity';
  }

  if (currentGroupBy === 'portfolio') {
    overrideGroupBy = 'adPortfolio';
  }

  if (overrideGroupBy && linkId) {
    const formattedLinkId = overrideGroupBy === 'product' ? linkId.toLocaleUpperCase() : linkId;
    const redirectParams = redirectToSamePage ? additionalParams : '';
    return `/${overrideGroupBy}/${formattedLinkId}${searchParams}${redirectParams}`;
  }

  return undefined;
};

export const shouldRenderLink = (groupByField: string, override?: string[]): boolean => {
  if (override) {
    return override.includes(groupByField);
  }
  return [
    'retailer',
    'client',
    'brand',
    'category',
    'subcategory',
    'segment',
    'product',
    'searchtermlist',
    'adCampaign',
    'adEntity',
    'adPortfolio'
  ].includes(groupByField);
};

const PLOT_RENDER_TIMEOUT = 10;
const SLIDE_STEP = 5;

// eslint-disable-next-line react/display-name
const TopEntityChartV2 = React.forwardRef(
  (
    {
      chartSeries,
      forecastedData,
      chartOptionsOverride,
      redirectToSamePage = true,
      seriesColors,
      leftAlignBars = true,
      containerSx = {},
      xAxisFormatter,
      xAxisLabelTextStyles = {},
      getCategories,
      useStaticCategory,
      reverseAxisStartPosition,
      maxDataPointsToShow = 5
    }: TopEntityV2ChartProps,
    ref
  ) => {
    const theme = useStacklineTheme();
    const overrideContext = useContext(OverrideContext);
    const formatMetric = useMetricFormatter();
    const chartRef = useRef(null);
    const [dataLabelsEnabled, setDataLabelsEnabled] = useState(false);
    const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
    const retailerId = useRetailerId();
    const selectedChartSeries = useMemo(() => forecastedData || chartSeries, [forecastedData, chartSeries]);
    const firstSeriesLength = chartSeries?.[0]?.data?.length - 1 || 0;
    const secondSeriesLength = chartSeries?.[1]?.data?.length - 1 || 0;

    const totalDataPoints = Math.max(firstSeriesLength, secondSeriesLength) || 0;
    // If we want to reverse the starting position of the xAxis, we need to calculate the initial range start differently
    const initialRangeStart = reverseAxisStartPosition ? Math.max(totalDataPoints - maxDataPointsToShow, 0) : 0;
    const [rangeStart, _setRangeStart] = useState(initialRangeStart);
    /**
     * This is the max index to show on the xAxis. Used for min/max in the xAxis options to enable scrolling.
     */
    const currentMaxIndex = Math.min(
      rangeStart + maxDataPointsToShow,
      leftAlignBars ? Math.max(5, totalDataPoints) : totalDataPoints
    );

    const currentGroupBy = selectedChartSeries?.[0]?.categories?.[0]?.type;

    useEffect(() => {
      _setRangeStart(initialRangeStart);
    }, [initialRangeStart, chartSeries]);

    const { getRetailerUrl } = useGetRetailerUrl();

    // Set time out is needed to prevent PLOT shows up before the chart renders
    const handleChartLoad = () => {
      setTimeout(() => {
        setDataLabelsEnabled(true);
      }, PLOT_RENDER_TIMEOUT);
    };

    // Set time out is needed to prevent PLOT shows up before the chart renders
    const handleChartRedraw = () => {
      setTimeout(() => {
        setDataLabelsEnabled(true);
      }, PLOT_RENDER_TIMEOUT);
    };

    const setRangeStart = (newRangeStart: number) => {
      _setRangeStart(newRangeStart);
      setDataLabelsEnabled(false);
    };

    const shiftLeft = () => {
      if (rangeStart >= SLIDE_STEP) {
        setRangeStart(rangeStart - SLIDE_STEP);
      } else if (rangeStart > 0) {
        setRangeStart(0);
      }
    };

    const shiftRight = () => {
      const newStart = Math.min(rangeStart + SLIDE_STEP, totalDataPoints - maxDataPointsToShow);
      if (newStart > rangeStart) {
        setRangeStart(newStart);
        setDataLabelsEnabled(false);
      }
    };

    useImperativeHandle(ref, () => ({
      api: {
        setRangeStart
      }
    }));

    const categoryData: EntityCategoryDatum[] = useMemo(() => {
      if (getCategories) {
        return getCategories(selectedChartSeries);
      } else {
        const categoryIdMap = {};
        const metricType = _get(selectedChartSeries, [0, 'metricType'], []);
        _get(selectedChartSeries, [0, 'categories'], []).forEach((category) => {
          categoryIdMap[category.name] = {
            id: category.id,
            retailerSku: category.retailerSku,
            stacklineSku: category.stacklineSku
          };
        });

        const secondChartSeries = _get(selectedChartSeries, [1, 'data'], []);

        secondChartSeries.forEach((data) => {
          const [categoryName, categoryValue] = data;
          if (categoryName in categoryIdMap) {
            categoryIdMap[categoryName] = {
              ...categoryIdMap[categoryName],
              currentValue: categoryValue
            };
          }
        });

        return _get(selectedChartSeries, [0, 'data'], []).map((row) => {
          const [displayName, comparisonValue] = row;

          const linkId = _get(categoryIdMap, [displayName, 'id'], null);
          const retailerSku = _get(categoryIdMap, [displayName, 'retailerSku'], null);
          const stacklineSku = _get(categoryIdMap, [displayName, 'stacklineSku'], null);
          const currentValue = _get(categoryIdMap, [displayName, 'currentValue'], 0);

          return {
            name: String(displayName),
            link: generateLink(
              currentGroupBy,
              retailerId,
              displayName,
              linkId,
              searchParams,
              redirectToSamePage,
              additionalParams
            ),
            newTab: currentGroupBy === 'searchterm',
            id: linkId,
            comparisonValue: comparisonValue ?? 0,
            currentValue,
            metricType,
            isSingleBarChart: secondChartSeries.length === 0,
            retailerSku: currentGroupBy === 'product' ? retailerSku : null,
            retailerLink: currentGroupBy === 'product' ? getRetailerUrl(stacklineSku) : null // Used for redirecting user to retailer page from the retailer sku even though we use the stackline SKU to generate it,
          };
        });
      }
    }, [
      getCategories,
      selectedChartSeries,
      currentGroupBy,
      retailerId,
      searchParams,
      redirectToSamePage,
      additionalParams,
      getRetailerUrl
    ]);

    /**
     * Remap the chart series data to keep the last week on the right side of the chart no matter how much we scroll.
     */
    const remappedChartSeries = selectedChartSeries.map((series) => {
      if (useStaticCategory) {
        // Only remap the data if the data length is greater than the currentMaxIndex.
        // For example, if we're on "last 4 weeks", we can only want to show the last 4 weeks of data rather than splice an additional duplicated end week into the data.
        if (series?.data?.length > currentMaxIndex) {
          const copyOfSeriesData = _cloneDeep(series?.data);
          // Splice the last element of the seriesData into the currentMaxIndex found here. This will keep the last week on the right side of the chart no matter how much we scroll.
          copyOfSeriesData.splice(currentMaxIndex, 1, series?.data[series?.data?.length - 1]);
          return { ...series, data: copyOfSeriesData };
        } else {
          return series;
        }
      }

      return null;
    });

    /**
     * Remap the categories to keep the last week on the right side of the chart no matter how much we scroll.
     */
    const remappedCategories = useMemo(() => {
      if (useStaticCategory) {
        if (selectedChartSeries?.[0]?.data?.length > currentMaxIndex) {
          // Splice the last element of the categoryData into the currentMaxIndex found here
          const copyOfCategories = _cloneDeep(categoryData);
          // Splice the last element of the categories into the currentMaxIndex found here. This will keep the last week on the right side of the chart no matter how much we scroll.
          copyOfCategories.splice(currentMaxIndex, 1, categoryData[categoryData.length - 1]);
          return copyOfCategories;
        } else {
          return categoryData;
        }
      }
      return null;
    }, [categoryData, useStaticCategory, currentMaxIndex, selectedChartSeries]);

    const textStyles: Highcharts.CSSObject = {
      fontFamily: theme.text.fontFamily,
      color: theme.colors.primary,
      fontSize: '12px',
      ...xAxisLabelTextStyles
    };

    const defaultOptions: Highcharts.Options = _merge(
      {
        chart: {
          animation: false,
          type: 'column',
          height: DEFAULT_TOP_ENTITY_HEIGHT,
          events: {
            load: handleChartLoad,
            redraw: handleChartRedraw
          },
          spacingTop: 0,
          marginBottom: 50
        },
        title: {
          text: ''
        },
        tooltip: {
          enabled: false
        },

        xAxis: [
          {
            type: 'category',
            categories: useStaticCategory ? remappedCategories : categoryData,
            min: rangeStart,
            max: Math.min(
              rangeStart + maxDataPointsToShow,
              leftAlignBars ? Math.max(5, totalDataPoints) : totalDataPoints
            ),
            tickLength: 0,
            lineColor: 'transparent',
            title: {
              text: ''
            },
            labels: {
              useHTML: true,
              formatter() {
                const {
                  id: entityId,
                  name,
                  link,
                  newTab,
                  retailerSku,
                  retailerLink,
                  comparisonValue,
                  currentValue,
                  metricType,
                  isSingleBarChart
                } = this.value;

                // contentScore and buyBox seller topEntity has no currentGroupBy and EntityId.
                if (!entityId && currentGroupBy && !xAxisFormatter) {
                  return '';
                }

                // this will respect the empty xAxis (to make left align when data is less than  maxDataPointsToShow)
                // e.g( highchart will always have max column value as 6 (xAxis max option), so if we have 4 data to show, highcharts will create 2, so we hide the xAxis from here
                // name === undefined flag is for align chart to the left side
                if (leftAlignBars && !currentGroupBy && name === undefined) {
                  return '';
                }

                const chartRefElement = chartRef?.current?.container?.current;
                const chartId = chartRefElement
                  ? chartRefElement.getAttribute('data-highcharts-chart')
                  : Math.random().toString(36);

                // if there is multiple chart in a same page, the document will have duplicated Id.
                const id = `box-${entityId}-${chartId}`; // Ensure unique ID
                const tooltipId = `tooltip-${entityId}-${chartId}`; // Ensure unique ID

                setTimeout(() => {
                  const element = document.getElementById(id);
                  const tooltipElement = document.getElementById(tooltipId);

                  // TODO:: As of Jan22 2025, tooltip is only available in Atlas, but Beacon have one with product entity
                  // TODO:: When we want to enable it in Beacon, simply remove the Beacon condition, but Review and Content might need extra work
                  if (
                    (element && tooltipElement && getAppName() === AppName.Atlas) ||
                    (getAppName() === AppName.Beacon && currentGroupBy === 'product')
                  ) {
                    element.addEventListener('mouseover', () => {
                      tooltipElement.style.display = 'block';
                    });
                    element.addEventListener('mouseout', () => {
                      tooltipElement.style.display = 'none';
                    });
                  }
                }, 1);

                if (xAxisFormatter) {
                  return xAxisFormatter(this.value, id, tooltipId);
                }

                // in case of group by campaign type, we need to format the name
                // we want this change to be applied to all top entity charts with campaign type group by
                // to be more readable, e.g. 'sponsoredProducts' => 'Sponsored Products'
                const formattedName =
                  currentGroupBy === campaignTypeGroupBy.id.toLocaleLowerCase() ? formatCapitalizedString(name) : name;

                return ReactDOMServer.renderToStaticMarkup(
                  <div id={id} style={{ position: 'relative' }}>
                    <DefaultXAxisLabel
                      name={String(formattedName)}
                      tooltipId={tooltipId}
                      isSingleBarChart={isSingleBarChart}
                      currentValue={currentValue}
                      comparisonValue={comparisonValue}
                      metricType={metricType}
                      link={shouldRenderLink(currentGroupBy, overrideContext) ? link : undefined}
                      newTab={newTab}
                      id={entityId}
                      retailerSku={retailerSku}
                      currentGroupBy={currentGroupBy}
                      retailerLink={retailerLink}
                      theme={theme}
                    />
                  </div>
                );
              },
              rotation: 0,
              step: 1,
              align: 'center',
              style: {
                ...textStyles,
                whiteSpace: 'normal',
                textOverflow: 'none'
              }
            }
          }
        ],
        credits: {
          enabled: false
        },
        legend: {
          enabled: false
        },
        yAxis: {
          visible: false,
          gridLineWidth: 0,
          title: {
            text: ''
          },
          maxPadding: 0.1
        },
        series: [
          ...(useStaticCategory && remappedChartSeries ? remappedChartSeries : selectedChartSeries).map(
            ({ data }, index) => ({
              // if the selected data is really new, then it returns datum[1] as undefined in value, so we need to set it to 0.
              data: data.map((datum) => (!datum[1] ? [datum[0], 0] : datum)),
              color: [_get(seriesColors, 0) || theme.colors.casper, _get(seriesColors, 1) || theme.colors.primary][
                index
              ],
              marker: seriesMarker,
              enableMouseTracking: false,
              // this enables animation for highcharts
              cropThreshold: 1500,
              borderRadius: 2,
              pointPadding: 0.1,
              groupPadding: 0.17,
              pointWidth: 24
            })
          )
        ],
        plotOptions: {
          series: {
            borderColor: 'transparent',
            states: {
              hover: {
                enabled: false
              }
            },
            dataLabels: {
              enabled: dataLabelsEnabled,
              allowOverlap: true,
              formatter() {
                return `${formatMetric(this.y, selectedChartSeries[0].metricType, {
                  decimalPlaces: 1,
                  showFullValue: selectedChartSeries[0].metricType === METRICTYPE.PERCENT
                })}`;
              },
              style: { ...textStyles },
              padding: 10,
              crop: false,
              overflow: 'none'
            }
          }
        }
      },
      typeof chartOptionsOverride === 'function' ? {} : chartOptionsOverride
    );
    const options = typeof chartOptionsOverride === 'function' ? chartOptionsOverride(defaultOptions) : defaultOptions;

    const chartWrapperHeight = Number(options?.chart?.height ?? DEFAULT_TOP_ENTITY_HEIGHT);
    const chevronTopHeight =
      chartWrapperHeight <= DEFAULT_TOP_ENTITY_HEIGHT
        ? 50 - (DEFAULT_TOP_ENTITY_HEIGHT - chartWrapperHeight) * 0.05
        : 50;

    const noData = chartSeries.every((series) => !series?.data?.length);

    return (
      <>
        <Box sx={{ position: 'relative', height: chartWrapperHeight, ...containerSx }}>
          <div
            style={{
              visibility: rangeStart === 0 ? 'hidden' : 'visible',
              position: 'absolute',
              top: `${chevronTopHeight}%`,
              left: '0',
              cursor: 'pointer'
            }}
            onClick={shiftLeft}
            role="button"
          >
            <ChevronIconV2 style={{ height: '24px', width: '24px', transform: 'rotate(180deg)' }} />
          </div>
          {noData ? (
            <Box sx={{ margin: '80px 0 0', display: 'flex', justifyContent: 'center' }}>
              <NoDataPlaceHolder
                title="Data currently unavailable"
                description="There is currently no data matching your selected criteria. Please try another query to view insights."
              />
            </Box>
          ) : (
            <HighchartsReact highcharts={Highcharts} options={options} ref={chartRef} />
          )}
          <div
            style={{
              visibility: rangeStart + maxDataPointsToShow < totalDataPoints ? 'visible' : 'hidden',
              position: 'absolute',
              top: `${chevronTopHeight}%`,
              right: '0',
              cursor: 'pointer'
            }}
            role="button"
            onClick={shiftRight}
          >
            <ChevronIconV2 style={{ height: '24px', width: '24px' }} />
          </div>
        </Box>
      </>
    );
  }
);

export default TopEntityChartV2;
